import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  setError,
  setFulfilled,
  setPending,
  withRequestStatus,
} from '../../../../shared/store-utils/request-status.feature';
import { withErrorHandler } from '../../../../shared/store-utils/error-handler.feature';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, exhaustMap, of, pipe, tap } from 'rxjs';
import { RegisterService } from '../services';
import { ITokenInformation, IRegisterUser } from '../interfaces';
import { tapResponse } from '@ngrx/operators';
import { ActivatedRoute } from '@angular/router';
import { PATH } from '../../../core/enums';
import { withRouter } from '../../../../shared/store-utils';
import { HttpErrorResponse } from '@angular/common/http';

export type RegisterState = {
  token: string | null;
  user: ITokenInformation | null;
};

export const initialState: RegisterState = {
  token: null,
  user: null,
};

export const RegisterStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withErrorHandler(),
  withRouter(),
  withProps(() => ({
    _registerService: inject(RegisterService),
    _activatedRoute: inject(ActivatedRoute),
  })),
  withComputed((store) => ({
    user$: computed(() => {
      const user = store.user();
      return user ? of(user) : EMPTY;
    }),
  })),
  withMethods((store) => {
    const register = rxMethod<IRegisterUser>(
      pipe(
        tap(() => patchState(store, setPending())),
        exhaustMap((value) => {
          return store._registerService.register(value).pipe(
            tapResponse({
              next: () => {
                patchState(store, setFulfilled());
                _onRequestSuccess();
              },
              error: (error: HttpErrorResponse) => {
                patchState(
                  store,
                  setError(error.error.message || 'Registration failed'),
                );
              },
            }),
          );
        }),
      ),
    );

    const getUserInformation = (token: string) => {
      patchState(store, { token });
      return store._registerService.getUserInformation(token).pipe(
        tapResponse({
          next: (userInfo) =>
            patchState(store, { user: userInfo }, setFulfilled()),
          error: (error: HttpErrorResponse) => {
            patchState(
              store,
              setError(
                error.error.message || 'Failed to load user information',
              ),
            );
            throw error;
          },
        }),
      );
    };

    function _onRequestSuccess(): Promise<boolean> {
      patchState(store, { token: null });
      return store.navigate(PATH.LOGIN);
    }
    return { register, getUserInformation };
  }),
);
